import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { TrendingUp, AlertTriangle, RefreshCw, BarChart3, Calendar, Shield, Clock, CheckCircle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';



const formatAmount = (n, currency = 'XOF') => {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(n);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatCard = ({ icon: IconComponent, label, value, sub, color, trend, trendValue }) => (
  <div className={`bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4 shadow-sm`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <IconComponent size={22} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
    {trend && (
      <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
        {trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
        {trendValue}
      </div>
    )}
  </div>
);

const ReserveThresholdBar = ({ label, current, threshold, currency = 'XOF' }) => {
  const pct = threshold > 0 ? Math.min(100, (current / threshold) * 100) : 0;
  const isLow = pct < 50;
  const isCritical = pct < 25;
  const barColor = isCritical ? 'bg-red-500' : isLow ? 'bg-amber-500' : 'bg-green-500';
  const textColor = isCritical ? 'text-red-700' : isLow ? 'text-amber-700' : 'text-green-700';
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className={`text-xs font-bold ${textColor}`}>{pct?.toFixed(1)}% du seuil</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
        <div className={`h-3 rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between text-xs text-gray-500">
        <span>Actuel: {formatAmount(current, currency)}</span>
        <span>Seuil: {formatAmount(threshold, currency)}</span>
      </div>
    </div>
  );
};

const FundsLiquidityDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [metrics, setMetrics] = useState({
    totalHeld: 0,
    releasedPendingTransfer: 0,
    completedTransfers: 0,
    failedTransfers: 0,
    reserveThresholds: [],
    forecastData: [],
    cycleBreakdown: []
  });
  const [selectedCycle, setSelectedCycle] = useState('all');

  const loadMetrics = useCallback(async () => {
    try {
      // Fetch escrow transactions for held funds
      const [escrowRes, payoutsRes, batchRunsRes, scheduleRes] = await Promise.all([
        supabase?.from('escrow_transactions')?.select('id, amount, status, currency, created_at')?.in('status', ['held', 'funded', 'pending_release', 'released'])?.order('created_at', { ascending: false }),
        supabase?.from('commission_payouts')?.select('id, net_payout_amount, payout_currency, routing_status, payout_channel, created_at, scheduled_at')?.order('created_at', { ascending: false })?.limit(200),
        supabase?.from('payout_batch_runs')?.select('*')?.order('created_at', { ascending: false })?.limit(12),
        supabase?.from('payout_schedule_configs')?.select('*')?.eq('is_active', true)
      ]);

      const escrow = escrowRes?.data || [];
      const payouts = payoutsRes?.data || [];
      const batchRuns = batchRunsRes?.data || [];
      const schedules = scheduleRes?.data || [];

      // Calculate held funds (escrow in held/funded/pending_release)
      const totalHeld = escrow?.filter(e => ['held', 'funded', 'pending_release']?.includes(e?.status))?.reduce((sum, e) => sum + (e?.amount || 0), 0);

      // Released but pending transfer (routing_status = pending or active)
      const releasedPendingTransfer = payouts?.filter(p => ['pending', 'active']?.includes(p?.routing_status))?.reduce((sum, p) => sum + (p?.net_payout_amount || 0), 0);

      // Completed transfers
      const completedTransfers = payouts?.filter(p => p?.routing_status === 'completed')?.reduce((sum, p) => sum + (p?.net_payout_amount || 0), 0);

      // Failed transfers
      const failedTransfers = payouts?.filter(p => p?.routing_status === 'failed')?.reduce((sum, p) => sum + (p?.net_payout_amount || 0), 0);

      // Reserve thresholds from schedule configs
      const reserveThresholds = schedules?.map(s => ({
        label: s?.cycle_type === 'weekly' ? 'Hebdomadaire' : s?.cycle_type === 'biweekly' ? 'Bimensuel' : 'Mensuel',
        current: payouts?.filter(p => p?.routing_status === 'pending')?.reduce((sum, p) => sum + (p?.net_payout_amount || 0), 0),
        threshold: s?.min_amount || 500000,
        currency: s?.currency || 'XOF'
      }));

      // If no schedule configs, show a default threshold
      if (reserveThresholds?.length === 0) {
        reserveThresholds?.push({
          label: 'Réserve Opérationnelle',
          current: releasedPendingTransfer,
          threshold: 5000000,
          currency: 'XOF'
        });
      }

      // Liquidity forecast by settlement cycle (last 6 months + next 3 months)
      const now = new Date();
      const forecastData = [];
      for (let i = -5; i <= 3; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
        const label = d?.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
        const monthPayouts = payouts?.filter(p => {
          const pd = new Date(p?.created_at);
          return pd?.getMonth() === d?.getMonth() && pd?.getFullYear() === d?.getFullYear();
        });
        const monthEscrow = escrow?.filter(e => {
          const ed = new Date(e?.created_at);
          return ed?.getMonth() === d?.getMonth() && ed?.getFullYear() === d?.getFullYear();
        });
        const isFuture = i > 0;
        forecastData?.push({
          label,
          fonds_retenus: monthEscrow?.filter(e => ['held', 'funded']?.includes(e?.status))?.reduce((s, e) => s + (e?.amount || 0), 0),
          liberes: monthPayouts?.filter(p => p?.routing_status === 'completed')?.reduce((s, p) => s + (p?.net_payout_amount || 0), 0),
          en_attente: monthPayouts?.filter(p => ['pending', 'active']?.includes(p?.routing_status))?.reduce((s, p) => s + (p?.net_payout_amount || 0), 0),
          isForecast: isFuture
        });
      }

      // Cycle breakdown from batch runs
      const cycleBreakdown = batchRuns?.slice(0, 8)?.map(r => ({
        id: r?.id,
        cycle: r?.cycle_type || 'monthly',
        totalAmount: r?.total_amount || 0,
        payoutCount: r?.payout_count || 0,
        status: r?.status || 'pending',
        runAt: r?.run_at || r?.created_at,
        currency: r?.currency || 'XOF'
      }));

      setMetrics({ totalHeld, releasedPendingTransfer, completedTransfers, failedTransfers, reserveThresholds, forecastData, cycleBreakdown });
    } catch (err) {
      console.error('Error loading funds metrics:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadMetrics(); }, [loadMetrics]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadMetrics();
  };

  const CYCLE_STATUS = {
    completed: { label: 'Complété', color: 'bg-green-100 text-green-700 border-green-200' },
    running: { label: 'En cours', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    failed: { label: 'Échoué', color: 'bg-red-100 text-red-700 border-red-200' }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)]?.map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                  <div className="h-6 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Tableau de Bord Liquidités & Fonds</h2>
          <p className="text-sm text-gray-500 mt-0.5">Vue consolidée des fonds retenus, libérés et prévisions de liquidité</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Shield}
          label="Fonds Totaux Retenus"
          value={formatAmount(metrics?.totalHeld)}
          sub="En séquestre actif"
          color="bg-blue-100 text-blue-700"
        />
        <StatCard
          icon={Clock}
          label="Libérés — Transfert Pending"
          value={formatAmount(metrics?.releasedPendingTransfer)}
          sub="En attente de virement"
          color="bg-amber-100 text-amber-700"
        />
        <StatCard
          icon={CheckCircle}
          label="Transferts Complétés"
          value={formatAmount(metrics?.completedTransfers)}
          sub="Virements réussis"
          color="bg-green-100 text-green-700"
        />
        <StatCard
          icon={AlertTriangle}
          label="Transferts Échoués"
          value={formatAmount(metrics?.failedTransfers)}
          sub="Nécessitent intervention"
          color="bg-red-100 text-red-700"
        />
      </div>

      {/* Reserve Thresholds */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
            <Shield size={18} className="text-purple-700" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Seuils de Réserve</h3>
            <p className="text-xs text-gray-500">Niveaux de liquidité par rapport aux seuils configurés</p>
          </div>
        </div>
        <div className="space-y-5">
          {metrics?.reserveThresholds?.map((rt, i) => (
            <ReserveThresholdBar key={i} {...rt} />
          ))}
        </div>
      </div>

      {/* Liquidity Forecast Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
              <TrendingUp size={18} className="text-indigo-700" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Prévisions de Liquidité</h3>
              <p className="text-xs text-gray-500">Historique 6 mois + prévisions 3 mois (zones grisées)</p>
            </div>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={metrics?.forecastData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000000 ? `${(v / 1000000)?.toFixed(1)}M` : v >= 1000 ? `${(v / 1000)?.toFixed(0)}K` : v} />
              <Tooltip
                formatter={(value, name) => [
                  formatAmount(value),
                  name === 'fonds_retenus' ? 'Fonds Retenus' : name === 'liberes' ? 'Libérés' : 'En Attente'
                ]}
              />
              <Legend formatter={v => v === 'fonds_retenus' ? 'Fonds Retenus' : v === 'liberes' ? 'Libérés' : 'En Attente'} />
              <Bar dataKey="fonds_retenus" fill="#3b82f6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="liberes" fill="#22c55e" radius={[3, 3, 0, 0]} />
              <Bar dataKey="en_attente" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Settlement Cycle Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center">
            <Calendar size={18} className="text-teal-700" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Cycles de Règlement</h3>
            <p className="text-xs text-gray-500">Historique des lots de paiement par cycle</p>
          </div>
        </div>
        {metrics?.cycleBreakdown?.length === 0 ? (
          <div className="p-10 text-center">
            <BarChart3 size={32} className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Aucun cycle de règlement enregistré</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cycle</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Montant Total</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nb Paiements</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {metrics?.cycleBreakdown?.map((row) => {
                  const st = CYCLE_STATUS?.[row?.status] || CYCLE_STATUS?.pending;
                  return (
                    <tr key={row?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5 font-medium text-gray-900 capitalize">{row?.cycle}</td>
                      <td className="px-5 py-3.5 text-gray-600">{formatDate(row?.runAt)}</td>
                      <td className="px-5 py-3.5 text-right font-semibold text-gray-900">{formatAmount(row?.totalAmount, row?.currency)}</td>
                      <td className="px-5 py-3.5 text-right text-gray-600">{row?.payoutCount}</td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${st?.color}`}>
                          {st?.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FundsLiquidityDashboard;
